<script setup lang="ts">
const {
  getFileEntryIcon,
  findByType,
  getFileEntryName,
  //getEditorRoute,
  //canOpenEditor,
} = useResourceDefinitions();
const { profile } = useProfile();
const { hasPermission, hasContentPermission, isInternalContentType } =
  usePermissions();
const { t } = useI18n({ useScope: "local" });
const defaultFileActions = ["view", "download", "copy", "delete"] as const;

type SiteFileAction = (typeof defaultFileActions)[number];
type SiteFileActionFilter =
  | SiteFileAction[]
  | ((file: SiteFile) => SiteFileAction[] | null | undefined);

const props = withDefaults(
  defineProps<{
    path: string;
    defaultNavigationRoute?: string | null;
    showBreadcrumbs?: boolean;
    filterTypes?: string[] | null;
    filterContentTypes?: string[] | null;
    includeMimeTypes?: string[] | null;
    excludeMimeTypes?: string[] | null;
    actionFilter?: SiteFileActionFilter | null;
    enableNavigation?: boolean;
    showBackButton?: boolean;
    selectMode?: boolean;
    hideList?: boolean;
  }>(),
  {
    enableNavigation: true,
    showBackButton: true,
  }
);

interface SiteFile {
  id: string;
  parentId: string;
  siteId: string;
  name: string;
  slug: string;
  path: string;
  type: string;
  length: number | null;
  children: SiteFile[];
  versions: any[];
  permissions: string[];
  metadata: Record<string, any>;
  parents: SiteFile[];
  flags?: {
    prevent_delete?: boolean;
  };
}

interface SiteFilesInjection {
  files: SiteFile | null;
  folderPath: string;
}

const route = useRoute();
const { site } = useSite();

const siteId = computed(() => {
  const routeSiteId = route.params.siteId;
  if (Array.isArray(routeSiteId)) {
    return routeSiteId[0] || site.value?.id || "";
  }
  return (routeSiteId as string) || site.value?.id || "";
});

let _defaultNavigationRoute = props.defaultNavigationRoute;
if (!props.defaultNavigationRoute) {
  _defaultNavigationRoute = `/${siteId.value}/files`;
}

const emit = defineEmits<{
  (
    e: "file-selected",
    file: {
      id: string;
      name: string;
      type: string;
      path: string;
      size?: number;
      // any other properties your files have
    }
  ): void;
  (e: "navigate", path: string): void;
}>();

const siteFiles = inject<SiteFilesInjection>("siteFiles", {
  files: null,
  folderPath: "",
});

const files = computed<SiteFile | null>(() => siteFiles.files);
const backParent = computed<SiteFile | null>(() => files.value?.parents?.[0] ?? null);

const skeletonRows = 5;
const isLoading = computed(() => files.value == null);
const profilePermissions = computed(() => profile.value?.permissions ?? []);

function matchesMimeType(
  fileType: string | null | undefined,
  patterns?: string[] | null
) {
  if (!fileType || !patterns?.length) {
    return false;
  }

  return patterns.some((pattern) => {
    if (pattern === "*") {
      return true;
    }
    if (pattern.endsWith("/*")) {
      const prefix = pattern.slice(0, -1);
      return fileType.startsWith(prefix);
    }
    return fileType === pattern;
  });
}

function hasIncludedMimeType(file: SiteFile) {
  if (props.filterTypes?.length && !props.filterTypes.includes(file.type)) {
    return false;
  }

  if (
    props.filterContentTypes?.length &&
    !matchesMimeType(file.type, props.filterContentTypes)
  ) {
    return false;
  }

  if (
    props.includeMimeTypes?.length &&
    !matchesMimeType(file.type, props.includeMimeTypes)
  ) {
    return false;
  }

  return true;
}

function isExcludedMimeType(file: SiteFile) {
  return matchesMimeType(file.type, props.excludeMimeTypes);
}

function canReadFile(file: SiteFile) {
  if (!hasPermission(profilePermissions.value, "files:read")) {
    return false;
  }

  if (file.type === "text/directory") {
    return true;
  }

  if (
    isInternalContentType(file.type) &&
    !hasPermission(profilePermissions.value, "internal:admin")
  ) {
    return false;
  }

  return hasContentPermission(profilePermissions.value, file.type, "read");
}

function canWriteFile(file: SiteFile) {
  if (!hasPermission(profilePermissions.value, "files:write")) {
    return false;
  }

  if (file.type === "text/directory") {
    return true;
  }

  if (
    isInternalContentType(file.type) &&
    !hasPermission(profilePermissions.value, "internal:admin")
  ) {
    return false;
  }

  return hasContentPermission(profilePermissions.value, file.type, "write");
}

function getAllowedActions(file: SiteFile) {
  const allowedActions = (() => {
    if (!props.actionFilter) {
      return defaultFileActions;
    }

    if (Array.isArray(props.actionFilter)) {
      return props.actionFilter;
    }

    return props.actionFilter(file) ?? defaultFileActions;
  })();

  return allowedActions.filter((action) =>
    action === "delete" ? canWriteFile(file) : canReadFile(file)
  );
}

function hasAction(file: SiteFile, action: SiteFileAction) {
  return getAllowedActions(file).includes(action);
}

function getActionChecker(file: SiteFile) {
  return (action: SiteFileAction) => hasAction(file, action);
}

const filteredChildren = computed(() => {
  const children: SiteFile[] = files.value?.children ?? [];
  return children.filter((file) => {
    if (!hasIncludedMimeType(file)) {
      return false;
    }

    if (isExcludedMimeType(file)) {
      return false;
    }

    return canReadFile(file);
  });
});

function handleItemClick(file: SiteFile) {
  if (!canReadFile(file)) {
    return;
  }

  if (file.type === "text/directory") {
    if (props.enableNavigation) {
      emit("navigate", file.path);
    }
    return;
  }

  emit("file-selected", file);

  if (!props.selectMode && props.enableNavigation) {
    emit("navigate", file.path);
  }
}

function handleBackClick() {
  if (backParent.value) {
    emit("navigate", backParent.value.path);
  }
}

const { add } = useBreadcrumbs();

watch(
  () => files.value?.parents,
  (parents) => {
    parents?.forEach((parent) => {
      if (parent.path) {
        add(
          getFileEntryName(parent),
          `${_defaultNavigationRoute}/${parent.path}`
        );
      }
    });

    if (files.value && files.value.name) {
      add(getFileEntryName(files.value), `${_defaultNavigationRoute}/${props.path}`);
    }
  },
  { immediate: true }
);

const breadcrumbs = computed(() => {
  // At root (no path), only show "Home"
  if (!props.path) {
    return [{ text: t("home"), to: `${_defaultNavigationRoute}/` }];
  }

  // Always start with Home
  const crumbs: { text: string; to: string }[] = [
    { text: t("home"), to: `${_defaultNavigationRoute}/` },
  ];

  // Add each parent (skip any with empty path, since Home is already added)
  files.value?.parents?.forEach((parent) => {
    if (parent.path) {
      crumbs.push({
        text: getFileEntryName(parent),
        to: `${_defaultNavigationRoute}/${parent.path}`,
      });
    }
  });

  // Finally, add the current folder as a non-link
  const segments = props.path.split("/").filter(Boolean);
  const currentName = segments[segments.length - 1];
  crumbs.push({
    text: getFileEntryName(files.value) || currentName,
    to: `${_defaultNavigationRoute}/${props.path}`,
  });

  return crumbs;
});

const { publicApiUrl } = useApiUrl();

// New function to copy asset URL to clipboard
const copyAssetUrl = (file: { path: string; type: string }) => {
  if (
    !hasPermission(profilePermissions.value, "files:read") ||
    (isInternalContentType(file.type) &&
      !hasPermission(profilePermissions.value, "internal:admin")) ||
    (file.type !== "text/directory" &&
      !hasContentPermission(profilePermissions.value, file.type, "read"))
  ) {
    return;
  }

  const url = `${publicApiUrl.value}/v1/file/${siteId.value}/download/${file.path}`;
  navigator.clipboard.writeText(url);
};

// Function to download a file and specify the filename
const downloadFile = async (file: {
  path: string;
  name: string;
  type: string;
}) => {
  if (
    !hasPermission(profilePermissions.value, "files:read") ||
    (isInternalContentType(file.type) &&
      !hasPermission(profilePermissions.value, "internal:admin")) ||
    (file.type !== "text/directory" &&
      !hasContentPermission(profilePermissions.value, file.type, "read"))
  ) {
    return;
  }

  try {
    const blob = await useApiFetch<Blob>(
      `/v1/file/${siteId.value}/download/${file.path}`,
      { responseType: "blob" }
    );

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Failed to download file", err);
  }
};

// Function to delete a file
const deleteAsset = async (file: {
  path: string;
  name: string;
  type: string;
}) => {
  if (
    !hasPermission(profilePermissions.value, "files:write") ||
    (isInternalContentType(file.type) &&
      !hasPermission(profilePermissions.value, "internal:admin")) ||
    (file.type !== "text/directory" &&
      !hasContentPermission(profilePermissions.value, file.type, "write"))
  ) {
    return;
  }

  try {
    await useApiFetch(`/v1/file/${file.path}`, {
      method: "DELETE",
      headers: { "x-site-id": siteId.value },
    });
  } catch (err) {
    console.error("Failed to delete file", err);
  }
};
</script>

<template>
  <div class="flex-1 space-y-4">
    <Breadcrumb v-if="showBreadcrumbs && breadcrumbs.length > 0">
      <BreadcrumbList>
        <template v-for="(crumb, idx) in breadcrumbs" :key="idx">
          <BreadcrumbItem>
            <BreadcrumbLink v-if="idx < breadcrumbs.length - 1" as-child>
              <nuxt-link :to="crumb.to">
                {{ crumb.text }}
              </nuxt-link>
            </BreadcrumbLink>
            <BreadcrumbPage v-else>
              {{ crumb.text }}
            </BreadcrumbPage>
            <BreadcrumbSeparator v-if="idx < breadcrumbs.length - 1" />
          </BreadcrumbItem>
        </template>
      </BreadcrumbList>
    </Breadcrumb>

    <slot name="header" />

    <ul v-if="!hideList" role="list" class="divide-y divide-gray-100">
      <template v-if="isLoading">
        <li
          v-for="n in skeletonRows"
          :key="`skeleton-${n}`"
          class="flex items-center p-4"
        >
          <Skeleton class="size-9 rounded-full" />
          <div class="ml-4 flex-1 space-y-2">
            <Skeleton class="h-4 w-1/3" />
            <Skeleton class="h-3 w-1/4" />
          </div>
          <Skeleton class="ml-6 h-4 w-16" />
          <div class="ml-6 flex items-center gap-2">
            <Skeleton class="h-8 w-8 rounded-md" />
            <Skeleton class="h-8 w-8 rounded-md" />
            <Skeleton class="h-8 w-8 rounded-md" />
          </div>
        </li>
      </template>

      <template v-else>
        <!-- "Back" only appears if there is a parent folder -->
        <li
          v-if="showBackButton && backParent"
          @click="handleBackClick"
          class="group flex items-center p-4 cursor-pointer hover:bg-primary-foreground"
        >
          <icon mode="svg"
            name="heroicons:arrow-small-left"
            class="text-4xl text-current group-hover:text-primary"
          />
          <div class="ml-4 space-y-1">
            <p class="text-sm font-medium leading-none">{{ t("back") }}</p>
            <p class="text-sm text-muted-foreground">{{ t("backHint") }}</p>
          </div>
        </li>

        <!-- List each child entry -->
        <li
          v-for="file in filteredChildren"
          :key="file.id"
          @click="handleItemClick(file)"
          class="group flex items-center p-4 cursor-pointer hover:bg-primary-foreground"
        >
          <img
            v-if="file.metadata.display_image"
            :src="`${publicApiUrl}/v1/file/${siteId.value}/download/${file.metadata.display_image}`"
            :alt="getFileEntryName(file)"
            class="size-9 rounded-full object-cover"
          />
          <icon mode="svg"
            v-else
            :name="getFileEntryIcon(file)"
            class="text-4xl text-primary"
          />

          <div class="ml-4 space-y-1" v-if="!file.type?.startsWith('audio/')">
            <p class="text-sm font-medium leading-none">
              {{ getFileEntryName(file) }}
            </p>
            <p class="text-sm text-muted-foreground">
              {{ findByType(file.type)?.name || file.type }}
            </p>
          </div>
          <site-files-audio-row
            v-else
            class="ml-4"
            :file="file"
            :site-id="siteId.value"
            :display-name="getFileEntryName(file)"
          />
          <div class="ml-auto">
            {{ file.length ? `${(file.length / 1024).toFixed(2)} KB` : "" }}
          </div>
          <div class="ml-6 flex items-center gap-2">
            <slot
              name="actions"
              :file="file"
              :allowed-actions="getAllowedActions(file)"
              :has-action="getActionChecker(file)"
            >
              <Button
                v-if="
                  hasAction(file, 'view') &&
                  file.type ===
                    'application/vnd.pivotalgroup.data.assessment.answer+json'
                "
                variant="ghost"
                size="icon"
                as-child
              >
                <nuxt-link
                  :to="`/${siteId.value}/data/preview/${file.path}`"
                  class="flex items-center gap-2"
                  @click.stop
                >
                  <icon mode="svg" name="heroicons:magnifying-glass" class="text-xl text-primary" />
                </nuxt-link>
              </Button> 
              <!-- <Button
                v-else-if="
                  hasAction(file, 'view') &&
                  canOpenEditor(file.type, profile?.emailAddress)
                "
                variant="ghost"
                size="icon"
                as-child
              >
                <nuxt-link
                  :to="`/${siteId.value}/${getEditorRoute(file.type)}/${file.path}`"
                  class="flex items-center gap-2"
                  @click.stop
                >
                  <icon mode="svg" name="heroicons:cog-6-tooth" class="text-xl" />
                </nuxt-link>
              </Button> -->
              <Button
                v-if="hasAction(file, 'download')"
                variant="ghost"
                size="icon"
                @click.stop="downloadFile(file)"
              >
                <icon mode="svg" name="heroicons:arrow-down-tray" class="text-xl" />
              </Button>
              <Button
                v-if="hasAction(file, 'copy')"
                variant="ghost"
                size="icon"
                @click.stop="copyAssetUrl(file)"
              >
                <icon mode="svg" name="heroicons:square-2-stack" class="text-xl" />
              </Button>
              <Dialog v-if="hasAction(file, 'delete')">
                <DialogTrigger as-child>
                  <Button
                    variant="ghost"
                    size="icon"
                    @click.stop
                    :disabled="file.flags?.prevent_delete"
                  >
                    <icon mode="svg"
                      name="heroicons:trash"
                      class="text-xl text-destructive"
                    />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Are you absolutely sure?</DialogTitle>
                    <DialogDescription>
                      This action cannot be undone. Are you sure you want to
                      permanently delete this file?
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="destructive"
                      @click="deleteAsset(file)"
                      type="submit"
                    >
                      Delete
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </slot>
          </div>
        </li>
      </template>
    </ul>

    <slot name="footer" />
  </div>
</template>
<i18n lang="yaml">
en:
  home: Home
  back: Back
  backHint: Navigate back one folder
</i18n>
